/**
 * "Update ready" toast for the web app (docs/web-updates, concept D).
 * Floats so it never shifts the page, and fades in rather than popping:
 * top-centre of the map on desktop game boards, bottom-right elsewhere on
 * desktop, along the bottom on phones. Phone game boards show a map strip
 * instead (components/game/WebUpdateStrip). Hidden during the viewer's own
 * turn; see lib/webUpdate for when it shows.
 */
import React, { useEffect, useRef } from 'react'
import { Animated, Platform, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native'
import { usePathname } from 'expo-router'
import { ThemeScope } from './ThemeScope'
import { DEFAULT_RESERVED_LEFT_DESKTOP, DESKTOP_BREAKPOINT } from '../../lib/mapDimensions'
import { UPDATE_TOAST_DESKTOP_MIN_WIDTH, refreshToLatest, snoozeUpdate, startWebUpdateWatcher, useWebUpdate } from '../../lib/webUpdate'

const DESKTOP_MIN_WIDTH = UPDATE_TOAST_DESKTOP_MIN_WIDTH
const DESKTOP_TOAST_WIDTH = 430
const FADE_IN_MS = 280
/** A game board (not its setup, chat or players pages). */
const GAME_BOARD_PATH = /^\/games\/[^/]+$/
/** The phone home screen pins New Game / Join Game along the bottom. */
const PHONE_HOME_BAR = 128

/** The Settings hub shows its own "new build is ready" row; no second prompt there. */
export function updateToastHiddenOn(pathname: string): boolean {
  return pathname === '/settings'
}

type Placement = { top?: number; bottom?: number; left?: number; right?: number; width?: number }

/**
 * Where the toast sits. Desktop game boards: top-centre of the map (right of
 * the 380 px side panel), clear of the map's bottom controls. Other desktop
 * pages: bottom-right. Phones: full width, above the home screen's bottom bar
 * (phone game boards use the map strip instead; see setInlineUpdateSlot).
 */
export function updateToastPlacement(width: number, pathname: string): Placement {
  const board = GAME_BOARD_PATH.test(pathname)
  if (board && width >= DESKTOP_BREAKPOINT) {
    const mapWidth = width - DEFAULT_RESERVED_LEFT_DESKTOP
    const toastWidth = Math.min(DESKTOP_TOAST_WIDTH, mapWidth - 32)
    return { top: 16, left: DEFAULT_RESERVED_LEFT_DESKTOP + (mapWidth - toastWidth) / 2, width: toastWidth }
  }
  if (width >= DESKTOP_MIN_WIDTH) return { bottom: 20, right: 20, width: DESKTOP_TOAST_WIDTH }
  return { bottom: 16 + (pathname === '/' ? PHONE_HOME_BAR : 0), left: 12, right: 12 }
}

export function WebUpdateToast() {
  const { showToast, inlineSlot } = useWebUpdate()
  const { width } = useWindowDimensions()
  const pathname = usePathname()

  useEffect(() => startWebUpdateWatcher(), [])

  if (Platform.OS !== 'web' || !showToast || inlineSlot || updateToastHiddenOn(pathname)) return null
  return (
    <ThemeScope style={{ position: 'absolute', zIndex: 9998, ...updateToastPlacement(width, pathname) }} pointerEvents="box-none">
      <FadeIn>
        <View testID="web-update-toast" accessibilityRole="alert"
          className="bg-paper-up border-2 border-olive rounded-xl px-3.5 py-3 flex-row items-center gap-3"
          style={{ shadowColor: '#000', shadowOpacity: 0.35, shadowRadius: 18, shadowOffset: { width: 0, height: 8 } }}>
          <View className="flex-1 gap-0.5">
            <Text className="text-ink font-stencil uppercase tracking-widest" style={{ fontSize: 12, lineHeight: 15 }}>Update ready</Text>
            <Text className="text-ink-soft font-typed" style={{ fontSize: 13, lineHeight: 17 }}>Refresh to get the latest build.</Text>
          </View>
          <TouchableOpacity testID="web-update-later" accessibilityRole="button" accessibilityLabel="Later"
            onPress={snoozeUpdate} className="border-2 border-olive rounded-lg px-3 py-2">
            <Text className="text-ink font-stencil uppercase" style={{ fontSize: 11, letterSpacing: 0.6 }}>Later</Text>
          </TouchableOpacity>
          <TouchableOpacity testID="web-update-refresh" accessibilityRole="button" accessibilityLabel="Refresh to update"
            onPress={() => refreshToLatest()} className="bg-blood rounded-lg px-3 py-2">
            <Text className="text-paper font-stencil uppercase" style={{ fontSize: 11, letterSpacing: 0.6 }}>Refresh</Text>
          </TouchableOpacity>
        </View>
      </FadeIn>
    </ThemeScope>
  )
}

/** Fades its child in on mount (each time the toast appears). */
function FadeIn({ children }: { children: React.ReactNode }) {
  const opacity = useRef(new Animated.Value(0)).current
  useEffect(() => {
    Animated.timing(opacity, { toValue: 1, duration: FADE_IN_MS, useNativeDriver: false }).start()
  }, [opacity])
  // Styles stay on the inner View: NativeWind classes don't apply to Animated components on web.
  return <Animated.View testID="web-update-toast-fade" style={{ opacity }}>{children}</Animated.View>
}
